import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useCreateCompanyForUserMutation,
  useJoinCompanyForUserMutation,
  useValidateInviteCodeQuery,
} from "../../services/api";
import { setCredentials } from "../../store/slices/authSlice";
import ErrorMessage from "../../Components/common/ErrorMessage";
import Loading from "../../Components/common/Loading";
import "./CompanySelection.css";

const CompanySelection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // Check if user already has a company - send to onboarding
  // (onboarding will redirect to dashboard if already completed)
  useEffect(() => {
    if (user?.company) {
      navigate("/onboarding");
    }
  }, [user, navigate]);

  // State for view mode
  const [viewMode, setViewMode] = useState("selection"); // selection, create, join

  // API mutations
  const [createCompany, { isLoading: isCreating, error: createError }] =
    useCreateCompanyForUserMutation();
  const [joinCompany, { isLoading: isJoining, error: joinError }] =
    useJoinCompanyForUserMutation();

  // Create company form state
  const [createFormData, setCreateFormData] = useState({
    name: "",
    slug: "",
    description: "",
  });

  // Join company form state
  const [inviteCode, setInviteCode] = useState("");
  const [shouldValidate, setShouldValidate] = useState(false);

  // Validate invite code
  const {
    data: validationData,
    isLoading: isValidating,
    error: validationError,
  } = useValidateInviteCodeQuery(inviteCode.replace("-", ""), {
    skip: !shouldValidate || inviteCode.length < 9,
  });

  const [validationErrorMsg, setValidationErrorMsg] = useState("");

  useEffect(() => {
    if (inviteCode.length >= 9) {
      setShouldValidate(true);
    } else {
      setShouldValidate(false);
    }
  }, [inviteCode]);

  const handleCreateChange = (e) => {
    const { name, value } = e.target;

    setCreateFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Auto-generate slug from company name
    if (name === "name") {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .trim();
      setCreateFormData((prev) => ({ ...prev, slug }));
    }

    if (validationErrorMsg) setValidationErrorMsg("");
  };

  const handleInviteCodeChange = (e) => {
    // Format invite code as ABCD-1234
    let formatted = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (formatted.length > 4) {
      formatted = formatted.slice(0, 4) + "-" + formatted.slice(4, 8);
    }
    setInviteCode(formatted);
    if (validationErrorMsg) setValidationErrorMsg("");
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setValidationErrorMsg("");

    if (!createFormData.name.trim()) {
      setValidationErrorMsg("Company name is required");
      return;
    }

    if (!createFormData.slug.trim()) {
      setValidationErrorMsg("Company slug is required");
      return;
    }

    try {
      const response = await createCompany({
        name: createFormData.name,
        slug: createFormData.slug,
        description: createFormData.description || undefined,
      }).unwrap();

      // Update credentials with new tokens
      if (response.user && response.tokens) {
        dispatch(setCredentials(response));
        navigate("/onboarding");
      }
    } catch (err) {
      console.error("Create company failed:", err);
    }
  };

  const handleJoinSubmit = async (e) => {
    e.preventDefault();
    setValidationErrorMsg("");

    if (!inviteCode || inviteCode.length < 9) {
      setValidationErrorMsg("Please enter a valid invite code");
      return;
    }

    try {
      const response = await joinCompany({
        inviteCode: inviteCode.replace("-", ""),
      }).unwrap();

      // Update credentials with new tokens
      if (response.user && response.tokens) {
        dispatch(setCredentials(response));
        navigate("/onboarding");
      }
    } catch (err) {
      console.error("Join company failed:", err);
    }
  };

  const handleSkip = () => {
    // Navigate to dashboard without company
    navigate("/dashboard");
  };

  // Selection view
  if (viewMode === "selection") {
    return (
      <div className="company-selection-page">
        <div className="company-selection-container">
          <div className="company-selection-card">
            <div className="company-selection-header">
              <h1>Welcome{user?.firstName ? `, ${user.firstName}` : ""}!</h1>
              <p>How would you like to get started?</p>
            </div>

            <div className="selection-options">
              <button
                className="selection-option create-option"
                onClick={() => setViewMode("create")}
              >
                <div className="option-icon">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                </div>
                <h3>Create a Company</h3>
                <p>Set up a new organization and invite your team</p>
              </button>

              <button
                className="selection-option join-option"
                onClick={() => setViewMode("join")}
              >
                <div className="option-icon">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <h3>Join a Company</h3>
                <p>Use an invite code to join an existing organization</p>
              </button>
            </div>

            <div className="skip-section">
              <button className="skip-btn" onClick={handleSkip}>
                Skip for now
              </button>
              <p className="skip-note">
                You can always set this up later from your settings
              </p>
            </div>

            <div className="company-selection-footer">
              <p>
                Wrong account?{" "}
                <a href="#/logout" className="signout-link">
                  Sign out
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Create company view
  if (viewMode === "create") {
    return (
      <div className="company-selection-page">
        <div className="company-selection-container">
          <div className="company-selection-card">
            <button className="back-btn" onClick={() => setViewMode("selection")}>
              Back
            </button>

            <div className="company-selection-header">
              <h1>Create Your Company</h1>
              <p>Set up your organization</p>
            </div>

            {createError && <ErrorMessage error={createError} />}
            {validationErrorMsg && (
              <div className="validation-error">{validationErrorMsg}</div>
            )}

            <form onSubmit={handleCreateSubmit} className="company-form">
              <div className="form-group">
                <label htmlFor="name">Company Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={createFormData.name}
                  onChange={handleCreateChange}
                  placeholder="Enter company name"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="slug">
                  Company URL Slug *
                  <span className="help-text">(auto-generated)</span>
                </label>
                <div className="slug-input-group">
                  <span className="slug-prefix">yourapp.com/</span>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={createFormData.slug}
                    onChange={handleCreateChange}
                    placeholder="company-slug"
                    required
                    pattern="[-a-z0-9]+"
                    title="Only lowercase letters, numbers, and hyphens allowed"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description (Optional)</label>
                <textarea
                  id="description"
                  name="description"
                  value={createFormData.description}
                  onChange={handleCreateChange}
                  placeholder="Brief description of your company"
                  rows="3"
                />
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isCreating}
              >
                {isCreating ? "Creating..." : "Create Company"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Join company view
  if (viewMode === "join") {
    return (
      <div className="company-selection-page">
        <div className="company-selection-container">
          <div className="company-selection-card">
            <button className="back-btn" onClick={() => setViewMode("selection")}>
              Back
            </button>

            <div className="company-selection-header">
              <h1>Join a Company</h1>
              <p>Enter your invite code</p>
            </div>

            {joinError && <ErrorMessage error={joinError} />}
            {validationErrorMsg && (
              <div className="validation-error">{validationErrorMsg}</div>
            )}

            <form onSubmit={handleJoinSubmit} className="company-form">
              <div className="form-group">
                <label htmlFor="inviteCode">Invite Code *</label>
                <input
                  type="text"
                  id="inviteCode"
                  value={inviteCode}
                  onChange={handleInviteCodeChange}
                  placeholder="ABCD-1234"
                  required
                  autoFocus
                  maxLength="9"
                  className="invite-code-input"
                />
              </div>

              {/* Show validation status */}
              {isValidating && (
                <div className="code-validation">
                  <Loading />
                  <span>Validating code...</span>
                </div>
              )}

              {validationData?.company && (
                <div className="company-preview">
                  <div className="company-preview-icon">&#10003;</div>
                  <div className="company-preview-info">
                    <h4>You're joining</h4>
                    <p className="company-name">{validationData.company.name}</p>
                    {validationData.company.description && (
                      <p className="company-desc">
                        {validationData.company.description}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {validationError && inviteCode.length >= 9 && (
                <div className="code-error">
                  <span>&#10007;</span>
                  <span>Invalid or expired invite code</span>
                </div>
              )}

              <button
                type="submit"
                className="submit-btn"
                disabled={isJoining || (inviteCode.length >= 9 && !validationData?.company)}
              >
                {isJoining ? "Joining..." : "Join Company"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CompanySelection;
